import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ImageGallery from "./tire-info/ImageGallery";
import { useTheme } from "@/context/ThemeContext";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { DamageImage } from "@/types/vehicleTest";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { GuestLockedSection } from "@/components/auth/GuestLockedSection";

interface DamagesProps {
  images: DamageImage[] | undefined;
  guestLocked?: boolean;
  onGuestLoginRequired?: () => void;
  /** When false, render content expanded (no accordion), e.g. for document/PDF view. */
  collapsible?: boolean;
  /** Optional: open clicked image in parent lightbox (DocumentView) */
  onOpenImage?: (args: { url: string; alt: string; index: number }) => void;
}

const Damages = ({
  images,
  collapsible = true,
  onOpenImage,
  guestLocked = false,
  onGuestLoginRequired,
}: DamagesProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { baseText, subtleText } = useTextColorClass();
  const { language } = useLanguage();
  const labels = carDetailsLabels(language).damages;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const damageData = (images || [])
    .filter((damage) => damage && damage.url)
    .map((damage, index) => ({
      id: damage.id || `damage_${index + 1}`,
      image: damage.url,
      fullscreenImage: (damage as any).fullSizeUrl || damage.url,
      comment: damage.comment || "",
    }));

  // Extract image URLs for gallery
  const imageUrls = damageData.map((damage) => damage.image);
  const fullscreenUrls = damageData.map((damage) => damage.fullscreenImage);

  // Get comment for current image
  const currentComment =
    damageData.length > 0 && damageData[currentImageIndex]
      ? damageData[currentImageIndex].comment
      : null;

  const content = (
    <div className="space-y-4">
      {damageData.length > 0 ? (
        <>
          <ImageGallery
            images={imageUrls}
            fullscreenImages={fullscreenUrls}
            title={labels.title}
            onImageChange={setCurrentImageIndex}
            onOpenImage={onOpenImage}
          />
          {currentComment && (
            <div
              className={`p-3 rounded-lg ${
                isDark ? "bg-gray-800" : "bg-gray-50"
              }`}
            >
              <p className={`text-sm md:text-sm ${subtleText}`}>
                <span className="font-medium">{labels.comment}</span>{" "}
                {currentComment}
              </p>
            </div>
          )}
        </>
      ) : (
        <p className={`${subtleText} text-sm md:text-sm`}>
          {labels.noImages}
        </p>
      )}
    </div>
  );

  const displayContent = (
    <GuestLockedSection locked={guestLocked} onLoginRequired={onGuestLoginRequired}>
      {guestLocked ? <div className="min-h-[8rem]" aria-hidden /> : content}
    </GuestLockedSection>
  );

  if (!collapsible) {
    return (
      <div className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}>
        <div className="p-4 md:p-6">
          <h2 className={`font-semibold text-sm md:text-base mb-4 ${baseText}`}>{labels.title}</h2>
          {displayContent}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="damages">
          <AccordionTrigger className={`px-4 py-4 md:py-3 hover:bg-accent/20 flex justify-between`}>
            <span className={`font-semibold text-base md:text-base ${baseText}`}>{labels.title}</span>
          </AccordionTrigger>
          <AccordionContent className={`p-4`}>
            {displayContent}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default Damages;
